/**
 * Estadísticas de una página.
 *
 * Sólo enseña lo que el dato sostiene: vistas por día, cuota sobre el sitio y
 * envíos de formulario hechos desde ella. No hay visitantes únicos por página
 * —`uniques` sólo se cuenta sobre el total del sitio— así que no se mencionan.
 * Un panel vacío con el nombre de la métrica encima invita a pensar que la
 * página no tuvo ninguno, que es peor que no ofrecerla.
 */
import { useEffect, useMemo, useState } from 'react'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { useI18n } from '../../lib/i18n'
import { CompareSelect, DateRangePicker } from './DateRangePicker'
import { TrafficChart } from './TrafficChart'
import { BarChart } from './BarChart'
import { analyticsQueryKey, getPageAnalytics, type AnalyticsQuery, type PageAnalytics } from '../../lib/api'
import {
  IconArrowLeft,
  IconChartBar,
  IconChartPie,
  IconClick,
  IconClipboardList,
  IconCopy,
  IconDots,
  IconExternalLink,
  IconEye,
  IconHome,
  IconLayout,
  IconLink,
  IconPencil,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react'

export type PageStatsActions = {
  onOpenEditor: () => void
  onOpenSettings: () => void
  onPreview: () => void
  onOpenPublished?: () => void
  onCopyLink?: () => void
  onDuplicate: () => void
  onRename: () => void
  onSetHome?: () => void
  onDelete?: () => void
}

/** Cambio porcentual, o null cuando no hay contra qué medirlo. */
function delta(current: number, previous: number): { label: string; up: boolean } | null {
  if (previous === 0) return null
  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct === 0) return null
  return { label: `${pct > 0 ? '+' : ''}${pct}%`, up: pct > 0 }
}

function Metric({ label, value, hint, icon: Icon, change }: {
  label: string
  value: string
  hint?: string
  icon: typeof IconClick
  change?: { label: string; up: boolean } | null
}) {
  return (
    <div className="dash-page-stat">
      <div className="dash-page-stat-head">
        {/* El rótulo como el título de una fila en el widget de Páginas —texto
            principal, 13 y seminegrita—, no como una nota gris. */}
        <span className="dash-page-stat-title">{label}</span>
        <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true"><Icon size={14} /></span>
      </div>
      {/* El dato en su caja, bajo el rótulo: el mismo doble fondo que los
          widgets, donde lo que se lee va dentro y el título queda fuera. */}
      <div className="dash-page-stat-body">
        <div className="dash-page-stat-value">
          <span className="dash-card-metric dash-analytics-metric">{value}</span>
          {change ? (
            <span className={`dash-page-stat-delta${change.up ? ' is-up' : ' is-down'}`}>{change.label}</span>
          ) : null}
        </div>
        {hint ? <span className="dash-card-meta">{hint}</span> : null}
      </div>
    </div>
  )
}

export function PageStats({ projectId, pageId, token, thumbnailUrl, published, pageUrl, actions, onBack }: {
  projectId: string
  pageId: string
  token: string
  thumbnailUrl?: string | null
  published: boolean
  /** URL pública, o null si el sitio aún no está publicado. */
  pageUrl: string | null
  actions?: PageStatsActions
  onBack: () => void
}) {
  const { t, fmt } = useI18n()
  const [query, setQuery] = useState<AnalyticsQuery>({ range: '30d', compare: 'previous' })
  const queryKey = analyticsQueryKey(query)
  const [stats, setStats] = useState<PageAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)

  const runAction = (action: () => void) => {
    setActionsOpen(false)
    action()
  }

  useEffect(() => {
    let alive = true
    setLoading(true)
    getPageAnalytics(token, projectId, pageId, query)
      .then((data) => { if (alive) { setStats(data); setFailed(false) } })
      .catch(() => { if (alive) setFailed(true) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
    // `queryKey` resume la consulta; `query` es un objeto nuevo en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pageId, token, queryKey])

  const comparisonLabel = useMemo(() => {
    if (!stats?.comparison) return null
    return query.compare === 'year' ? t('range.compare.year') : t('range.compare.previous')
  }, [stats?.comparison, query.compare, t])

  const share = stats ? Math.round(stats.views.shareOfSite * 1000) / 10 : 0

  return (
    <div className="dash-projects-view dash-pages-view dash-page-stats-view">
      {/* De dónde vienes y qué pantalla es ésta. El nombre de la página no va
          aquí: está justo debajo, con su miniatura, como en la ficha de una
          oferta. Así la barra sólo tiene que sostener la salida. */}
      <DashboardPageHeader
        title={t('nav.pages')}
        subtitle={t('pageStats.subtitle')}
        actions={(
          <button className="dash-secondary-btn dash-detail-back" type="button" onClick={onBack}><IconArrowLeft size={14} /> {t('pageStats.back')}</button>
        )}
      />

      {/*
        Quién es esta página, con la misma cabecera que la ficha de una oferta
        —`dash-record-hero`—: miniatura, nombre con su estado, su dirección, y a
        la derecha el enlace y lo que se hace con ella.

        Antes esto eran dos piezas: los mandos arriba, en la barra de sección, y
        la identidad en un bloque suelto debajo. Quien miraba tenía el nombre en
        un sitio, la dirección en otro y el botón de abrirla en un tercero.
      */}
      <section className="dash-record-hero" aria-label={stats?.page.name ?? t('pageStats.fallbackName')}>
        <span className="dash-record-hero-cover is-static">
          {thumbnailUrl ? <img src={thumbnailUrl} alt="" loading="lazy" /> : <IconLayout size={18} aria-hidden="true" />}
        </span>

        <div className="dash-record-hero-copy">
          <div className="dash-record-hero-titleline">
            <h2 title={stats?.page.name ?? undefined}>{stats?.page.name ?? t('pageStats.fallbackName')}</h2>
            {/* Publicada o no, en la misma insignia que usa el resto del panel
                para decir si algo se ve desde fuera. */}
            <span className={`dash-offer-status is-${published ? 'active' : 'draft'}`}>
              {published ? t('pageStats.published') : t('pageStats.draft')}
            </span>
          </div>

          {/* La dirección, que es lo que describe a una página: su nombre es
              interno y la ruta es lo que teclea quien la visita. */}
          <p title={pageUrl ?? stats?.page.path ?? undefined}>
            {pageUrl ? pageUrl.replace(/^https?:\/\//, '') : stats?.page.path ?? '—'}
          </p>

          {/* Y su ficha en datos sueltos, como el precio y los archivos en una
              oferta: cuándo nació y cuándo se tocó por última vez. */}
          <ul className="dash-record-hero-meta">
            <li>{t('pageStats.created')} {stats ? fmt.date(stats.page.createdAt) : '—'}</li>
            <li>{t('pageStats.updated')} {stats ? fmt.date(stats.page.updatedAt) : '—'}</li>
          </ul>
        </div>

        <div className="dash-record-hero-actions">
          {actions ? (
            <>
              {/* Sin «Copiar» ni «Abrir» sueltos: los dos están en el menú de
                  al lado, con el resto de lo que se hace con una página. Aquí
                  eran una tercera y una cuarta caja para acciones que ya tienen
                  sitio, y en una columna estrecha eran justo las que aplastaban
                  el nombre y la dirección. En una oferta sí van fuera, porque
                  allí su enlace ES el producto y no hay menú donde buscarlo. */}
              <button className="dash-primary-btn" type="button" onClick={actions.onOpenEditor}>
                <IconLayout size={15} /> {t('pageStats.openEditor')}
              </button>

              <div className={`dash-project-row-menu-wrap${actionsOpen ? ' is-open' : ''}`}>
                  <button
                    className={`project-menu-btn dash-page-options-btn dash-page-stats-options-btn${actionsOpen ? ' active' : ''}`}
                    type="button"
                    aria-label={t('pageStats.menuAria')}
                    aria-haspopup="menu"
                    aria-expanded={actionsOpen}
                    onClick={() => setActionsOpen((open) => !open)}
                  >
                    <IconDots size={16} />
                  </button>
                  {actionsOpen ? (
                    <>
                      <div className="project-menu-backdrop" role="presentation" onClick={() => setActionsOpen(false)} />
                      <div className="project-menu dash-project-row-menu" role="menu">
                        <button className="project-menu-item" type="button" onClick={() => runAction(actions.onOpenEditor)}><IconLayout size={14} /> {t('pageStats.menu.editor')}</button>
                        <button className="project-menu-item" type="button" onClick={() => runAction(actions.onOpenSettings)}><IconSettings size={14} /> {t('pageStats.menu.settings')}</button>
                        <button className="project-menu-item" type="button" disabled aria-current="page"><IconChartBar size={14} /> {t('pageStats.menu.stats')}</button>
                        <div className="project-menu-rule" />
                        <button className="project-menu-item" type="button" onClick={() => runAction(actions.onPreview)}><IconEye size={14} /> {t('pageStats.menu.preview')}</button>
                        {actions.onOpenPublished ? <button className="project-menu-item" type="button" onClick={() => runAction(actions.onOpenPublished!)}><IconExternalLink size={14} /> {t('pageStats.menu.published')}</button> : null}
                        {actions.onCopyLink ? <button className="project-menu-item" type="button" onClick={() => runAction(actions.onCopyLink!)}><IconLink size={14} /> {t('pageStats.menu.copyLink')}</button> : null}
                        <div className="project-menu-rule" />
                        <button className="project-menu-item" type="button" onClick={() => runAction(actions.onDuplicate)}><IconCopy size={14} /> {t('pageStats.menu.duplicate')}</button>
                        <button className="project-menu-item" type="button" onClick={() => runAction(actions.onRename)}><IconPencil size={14} /> {t('pageStats.menu.rename')}</button>
                        {actions.onSetHome ? <button className="project-menu-item" type="button" onClick={() => runAction(actions.onSetHome!)}><IconHome size={14} /> {t('pageStats.menu.setHome')}</button> : null}
                        {actions.onDelete ? <button className="project-menu-item danger" type="button" onClick={() => runAction(actions.onDelete!)}><IconTrash size={14} /> {t('pageStats.menu.delete')}</button> : null}
                      </div>
                    </>
                  ) : null}
              </div>
            </>
          ) : null}
        </div>
      </section>

      {/* Rótulo a la izquierda y controles a la derecha: dice sobre qué mandan
          los dos selectores, que sueltos en una esquina no era evidente. */}
      <div className="dash-page-stats-controls">
        <h3 className="dash-page-stats-heading">{t('pageStats.heading')}</h3>
        <div className="dash-toolbar dash-layered-toolbar dash-analytics-range-toolbar">
          <DateRangePicker query={query} window={stats?.window} timezone={stats?.timezone} onChange={setQuery} />
          <CompareSelect query={query} onChange={setQuery} />
        </div>
      </div>

      {failed ? (
        <div className="dash-placeholder dash-placeholder--inset"><span>{t('pageStats.loadFailed')}</span></div>
      ) : (
        <>
          <div className="dash-page-stats-metrics">
            <Metric
              label={t('pageStats.views')}
              value={stats ? fmt.number(stats.views.total) : '—'}
              icon={IconClick}
              change={stats && stats.comparison ? delta(stats.views.total, stats.views.totalPrev) : null}
            />
            <Metric
              label={t('pageStats.share')}
              value={stats ? `${share}%` : '—'}
              hint={t('pageStats.share.hint')}
              icon={IconChartPie}
            />
            <Metric
              label={t('pageStats.forms')}
              value={stats ? fmt.number(stats.forms.total) : '—'}
              icon={IconClipboardList}
              change={stats && stats.comparison ? delta(stats.forms.total, stats.forms.totalPrev) : null}
            />
          </div>

          <TrafficChart
            series={stats?.views.series ?? []}
            comparison={stats?.views.comparison ?? []}
            comparisonLabel={comparisonLabel}
            timezone={stats?.timezone}
            loading={loading}
          />

          <div className="dash-page-stats-forms">
            <div className="dash-analytics-chart-head">
              <div className="dash-analytics-card-heading">
                <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true"><IconClipboardList size={14} /></span>
                <div>
                  <h3 className="dash-card-title">{t('pageStats.forms')}</h3>
                  <span className="dash-card-meta">{t('pageStats.forms.meta')}</span>
                </div>
              </div>
            </div>
            {/* El gráfico en su caja, como el dato de cada métrica. */}
            <div className="dash-stat-chart-box">
              <BarChart series={stats?.forms.series ?? []} loading={loading} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
